import { Button, FormError, QueryView } from "@simplicity/ui";
import { useState } from "react";
import { AgentSetupPanel } from "./AgentSetupPanel";
import { AgentBadge, PausedBadge, WorkspaceStatusBadge } from "./OnsiteBadges";
import { agentBehindHint, agentImageLine, approvablePlan, isActiveRun } from "./onsiteModel";
import { OnsiteRunsTable } from "./OnsiteRunsTable";
import { RunDetail } from "./RunDetail";
import { SftpUsersPanel } from "./SftpUsersPanel";
import type { OnsiteAgent, OnsiteRun, OnsiteWorkspace } from "./types";
import { useRequestPlan, useRuns } from "./useOnsite";
import { WorkspaceSettings } from "./WorkspaceSettings";

function WorkspaceFacts({ workspace }: { workspace: OnsiteWorkspace }) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
      <dt className="text-muted-foreground">Agent service account</dt>
      <dd className="break-all font-mono text-xs text-foreground">
        {workspace.agentServiceAccount}
      </dd>
      <dt className="text-muted-foreground">State bucket</dt>
      <dd className="break-all font-mono text-xs text-foreground">{workspace.stateBucket}</dd>
      <dt className="text-muted-foreground">SSH source ranges</dt>
      <dd className="font-mono text-xs text-foreground">
        {workspace.enableSftpServer ? workspace.sshSourceRanges.join(", ") || "—" : "SFTP off"}
      </dd>
    </dl>
  );
}

function AgentImage({ agent }: { agent: OnsiteAgent }) {
  const hint = agentBehindHint(agent);
  return (
    <div className="text-sm">
      <p className="text-muted-foreground">{agentImageLine(agent)}</p>
      {hint && <p className="text-foreground">{hint}</p>}
    </div>
  );
}

function RequestPlan({ workspace, blocked }: { workspace: OnsiteWorkspace; blocked: boolean }) {
  const plan = useRequestPlan(workspace.id);
  const disabled = blocked || plan.isPending || workspace.status === "disabled";
  return (
    <div className="space-y-2">
      <Button onClick={() => plan.mutate()} disabled={disabled}>
        {plan.isPending ? "Requesting..." : "Request plan"}
      </Button>
      {plan.error && <FormError assertive>{plan.error.message}</FormError>}
    </div>
  );
}

function RunsSection({ workspace, runs }: { workspace: OnsiteWorkspace; runs: OnsiteRun[] }) {
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const approvable = approvablePlan(runs);
  const blocked = runs.some(isActiveRun);
  // While a run is active nothing can be approved, so lead with the newest run instead.
  const fallback = (blocked ? null : approvable) ?? runs.at(0) ?? null;
  const shown = runs.find((run) => run.id === selectedRunId) ?? fallback;
  return (
    <div className="space-y-4">
      <SftpUsersPanel workspace={workspace} blocked={blocked} />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <h3 className="text-lg text-foreground">Recent runs</h3>
        <RequestPlan workspace={workspace} blocked={blocked} />
      </div>
      <OnsiteRunsTable
        runs={runs}
        selectedRunId={shown?.id ?? null}
        approvableRunId={approvable?.id ?? null}
        onSelect={setSelectedRunId}
      />
      {shown && (
        <RunDetail
          run={shown}
          approvable={shown.id === approvable?.id}
          blocked={blocked}
          workspaceId={workspace.id}
          targetLabel={`${workspace.name} (${workspace.gcpProjectId})`}
          agent={workspace.agent}
        />
      )}
    </div>
  );
}

export function WorkspaceDetail({ workspace }: { workspace: OnsiteWorkspace }) {
  const runsQuery = useRuns(workspace.id);
  return (
    <section className="space-y-6" aria-label={`Workspace ${workspace.name}`}>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <h2 className="text-xl text-foreground">{workspace.name}</h2>
          <WorkspaceStatusBadge status={workspace.status} />
          <PausedBadge workspace={workspace} />
          <AgentBadge agent={workspace.agent} />
          <span className="text-sm text-muted-foreground">
            {workspace.projectName} · {workspace.gcpProjectId}
          </span>
        </div>
        <AgentImage agent={workspace.agent} />
        <WorkspaceFacts workspace={workspace} />
        <WorkspaceSettings workspace={workspace} />
      </div>
      <AgentSetupPanel workspace={workspace} />
      <QueryView
        query={runsQuery}
        loading={<p className="text-sm text-muted-foreground">Loading runs…</p>}
        error={(err) => <FormError assertive>Failed to load runs: {err.message}</FormError>}
      >
        {(runs) => <RunsSection workspace={workspace} runs={runs} />}
      </QueryView>
    </section>
  );
}
